/**
 * ACL (Access Control List) Middleware (Targeted)
 * Checks role/permission access for protected routes
 * Apply to specific routes via: definePageMeta({ middleware: ['auth', 'acl'] })
 *
 * NEW PERMISSION SYSTEM (v2):
 * - requiredPermission: { resource: 'camps', action: 'read' }
 * - requiredPermissions: [{ resource: 'camps', action: 'read' }, { resource: 'events', action: 'read' }]
 * - permissionMode: 'any' | 'all' (default: 'any')
 *
 * Note: This middleware should be used AFTER 'auth' middleware
 * ZERO TRUST: Uses server-side API to check permissions
 */
import { logApiError, logWarn } from '~/utils/logger'

// Type for permission requirement
interface PermissionRequirement {
  resource: string
  action: string
}

// Extended user data with new permissions format
interface UserDataWithPermissions {
  id: string
  email?: string
  roles: string[]
  permissions?: Record<string, Record<string, boolean>>
  security_level?: number
  [key: string]: unknown
}

export default defineNuxtRouteMiddleware(async to => {
  // For routes with ssr: false, skip server-side ACL checks
  // The ACL will be checked client-side after hydration
  if (import.meta.server && to.meta.ssr === false) {
    return
  }

  // Skip if route is public or unauthenticated only
  if (to.meta.public || to.meta.unauthenticatedOnly)
    return

  // On client-side, check if user is authenticated first
  if (import.meta.client) {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      // User is not authenticated - redirect to login
      return navigateTo({
        name: 'auth-login',
        query: { redirect: to.fullPath },
      })
    }
  }

  try {
    // Get user data from /api/me (uses server-side RLS)
    // Pass cookies on server-side to ensure session is recognized
    const headers = import.meta.server ? useRequestHeaders(['cookie']) : {}

    const userData = await $fetch<UserDataWithPermissions>('/api/me', {
      headers,
      retry: 0, // Don't retry on 401 errors - session likely expired
    })

    // Helper function to check if user has a specific permission
    const hasPermission = (resource: string, action: string): boolean => {
      const perms = userData.permissions?.[resource]
      if (!perms)
        return false

      // Check exact permission or manage (which includes all)
      return perms[action] || (action !== 'manage' && perms.manage) || false
    }

    // For admin panel routes, check if user has admin access
    if (to.path.startsWith('/admin')) {
      // Check if user has system:read or system:manage permission
      // OR has any permissions at all (meaning they're assigned a role with permissions)
      const hasAdminAccess = hasPermission('system', 'read')
        || hasPermission('system', 'manage')
        || Object.keys(userData.permissions || {}).length > 0

      if (!hasAdminAccess) {
        logWarn(`User ${userData.email} denied access to admin panel - no admin permissions`)

        return navigateTo({ name: 'not-authorized' })
      }
    }

    // Check route-specific permissions from meta
    const meta = to.meta

    // NEW: Check required permission (single)
    if (meta.requiredPermission) {
      const perm = meta.requiredPermission as PermissionRequirement
      if (!hasPermission(perm.resource, perm.action)) {
        logWarn(`User ${userData.email} denied access - missing permission: ${perm.resource}:${perm.action}`)

        return navigateTo({ name: 'not-authorized' })
      }
    }

    // NEW: Check required permissions (multiple)
    if (meta.requiredPermissions) {
      const perms = meta.requiredPermissions as PermissionRequirement[]
      const mode = (meta.permissionMode as 'any' | 'all') || 'any'

      if (mode === 'all') {
        // User needs ALL permissions
        const hasAll = perms.every(p => hasPermission(p.resource, p.action))
        if (!hasAll) {
          logWarn(`User ${userData.email} denied access - missing some required permissions`)

          return navigateTo({ name: 'not-authorized' })
        }
      }
      else {
        // User needs ANY permission (default)
        const hasAny = perms.some(p => hasPermission(p.resource, p.action))
        if (!hasAny) {
          logWarn(`User ${userData.email} denied access - missing all required permissions`)

          return navigateTo({ name: 'not-authorized' })
        }
      }
    }
  }
  catch (error) {
    logApiError('/middleware/acl', error, { route: to.fullPath })

    const errorWithStatus = error as { status?: number, statusCode?: number }

    // If 401 Unauthorized, redirect to login instead of not-authorized
    // This handles session expiration more gracefully
    if (errorWithStatus.status === 401 || errorWithStatus.statusCode === 401) {
      logWarn('Session expired or invalid - redirecting to login')

      return navigateTo({
        name: 'auth-login',
        query: { redirect: to.fullPath },
      })
    }

    // For other errors, go to not-authorized
    return navigateTo({ name: 'not-authorized' })
  }
})
